import { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  FormFieldSet,
  showToast,
  useFormHandler,
} from "../../../../../components";
import { ENDPOINTS } from "../../../../../redux/apiConfig";
import { useDispatch } from "react-redux";
import {
  getCompanyDetailsStart,
  saveCompanyDetailsStart,
} from "../../../../../redux/slices/setting/settingsSlice";

const emptyContact = {
  contacts: [
    {
      firstName: "",
      lastName: "",
      designation: "",
      mobile: { code: "", number: "" },
      primaryEmail: [],
      secondaryEmail: [],
    },
  ],
};

const contactValidation = {
  contacts: [
    {
      firstName: {
        type: "text",
        required: true,
        message: "First Name is required",
      },
      primaryEmail: {
        type: "email",
        required: true,
        message: "Primary Email is required",
      },
      designation: { type: "text", required: true },
    },
  ],
};

const buildMobilePayload = (mobile) => {
  if (!mobile?.code || !mobile?.number) return null;

  const parsedNumber = parseInt(mobile.number, 10);
  if (isNaN(parsedNumber)) return null;

  return {
    primary: { code: mobile.code, number: parsedNumber },
    secondary: null,
  };
};

const extractMobileFormValue = (mobileObj) => {
  if (!mobileObj?.primary) return { code: "", number: "" };

  return {
    code: mobileObj.primary.code || "",
    number: String(mobileObj.primary.number || ""),
  };
};

const toEmailString = (val) =>
  Array.isArray(val) ? (val[0] ?? "") : (val ?? "");

const ContactInformation = () => {
  const { user } = useSelector((state) => state?.auth);
  const dispatch = useDispatch();
  const { generalDetails } = useSelector(
    (state) => state.settings.companyProfile,
  );

  const { formData, errors, setFormData, validateAllFields, handleChange } =
    useFormHandler(emptyContact, contactValidation);

  useEffect(() => {
    dispatch(
      getCompanyDetailsStart({
        coll: "Organization",
        method: "GET",
        fil: {
          uiid: { type: "NONE", value: user?.data?.organizationId?.uiid },
        },
        url: ENDPOINTS.ORGANIZATIONS.GET,
      }),
    );
  }, [dispatch, user?.data?.organizationId?.uiid]);

  useEffect(() => {
    const contactInfo = generalDetails?.contactInformation;
    if (contactInfo?.length > 0) {
      const mapped = contactInfo.map((c) => ({
        firstName: c.firstName || "",
        lastName: c.lastName || "",
        designation: c.designation || "",
        mobile: extractMobileFormValue(c.mobile),
        primaryEmail: c.email?.primary ? [c.email.primary] : [],
        secondaryEmail: c.email?.secondary ? [c.email.secondary] : [],
      }));

      setFormData({ contacts: mapped });
    }
  }, [generalDetails, setFormData]);

  const addContact = () => {
    setFormData((prev) => ({
      contacts: [
        ...prev.contacts,
        {
          firstName: "",
          lastName: "",
          designation: "",
          mobile: { code: "", number: "" },
          primaryEmail: [],
          secondaryEmail: [],
        },
      ],
    }));
  };

  const onRemove = (index) => {
    const updated = [...formData.contacts];
    updated.splice(index, 1);

    setFormData({ contacts: updated });
  };

  const onChange = (index, field, value) => {
    const updated = [...formData.contacts];

    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    setFormData({ contacts: updated });
  };

  const isContactEmpty = (c) =>
    !(
      c.firstName?.trim() ||
      c.lastName?.trim() ||
      c.designation?.trim() ||
      c.mobile?.number ||
      c.primaryEmail?.[0] ||
      c.secondaryEmail?.[0]
    );

  const handleSubmit = () => {
    const isValid = validateAllFields();
    if (!isValid) {
      showToast("Please fill required fields", "error");
      return;
    }
    const validContacts = formData.contacts.filter((c) => !isContactEmpty(c));

    const contactInformation = validContacts.map((c) => ({
      firstName: c.firstName,
      lastName: c.lastName,
      designation: c.designation || undefined,
      mobile: buildMobilePayload(c.mobile),
      email: {
        primary: toEmailString(c.primaryEmail),
        ...(toEmailString(c.secondaryEmail)
          ? { secondary: toEmailString(c.secondaryEmail) }
          : {}),
      },
    }));

    const payload = {
      id: user?.data?.organizationId?._id,
      payload: { $set: { contactInformation } },
    };

    dispatch(saveCompanyDetailsStart(payload));
  };

  return (
    <div className="mt-4 overflow-auto flex flex-1 flex-col">
      <div className="pr-1">
        {formData?.contacts?.map((contact, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-xl p-5 mb-4 relative bg-white shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-gray-500 uppercase">
                Contact {index + 1}
              </span>

              {formData?.contacts?.length > 1 && (
                <Button
                  title="Remove"
                  icon="remove"
                  variant="outlined"
                  size="sm"
                  onClick={() => onRemove(index)}
                />
              )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <FormFieldSet
                type="text"
                title="First Name"
                placeholder="Enter First Name"
                required
                shadow
                value={contact.firstName}
                onChange={(v) => onChange(index, "firstName", v)}
                error={errors?.contacts?.[index]?.firstName}
              />

              <FormFieldSet
                type="text"
                title="Last Name"
                placeholder="Enter Last Name"
                shadow
                value={contact.lastName}
                onChange={(v) => onChange(index, "lastName", v)}
              />

              <FormFieldSet
                type="text"
                title="Designation"
                placeholder="e.g. CEO, Manager"
                shadow
                value={contact.designation}
                onChange={(v) => onChange(index, "designation", v)}
                error={errors?.contacts?.[index]?.designation}
                required
              />

              <FormFieldSet
                type="phone"
                title="Mobile"
                shadow
                value={contact.mobile?.number}
                code={contact.mobile?.code}
                onChange={(full, details) =>
                  onChange(index, "mobile", {
                    code: details.countryCode,
                    number: details.number,
                  })
                }
              />

              <FormFieldSet
                type="email"
                title="Primary Email"
                placeholder="Enter Primary Email"
                required
                shadow
                value={[contact.primaryEmail?.[0]]}
                onChange={(v) => onChange(index, "primaryEmail", [v])}
                error={errors?.contacts?.[index]?.primaryEmail}
              />

              <FormFieldSet
                type="email"
                title="Secondary Email"
                placeholder="Optional"
                shadow
                value={[contact.secondaryEmail?.[0]] || ""}
                onChange={(v) => onChange(index, "secondaryEmail", [v])}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 justify-between pt-5">
        <Button
          title="Add Another Contact"
          icon="add"
          variant="outlined"
          onClick={addContact}
        />

        <Button title="Save" onClick={handleSubmit} />
      </div>
    </div>
  );
};

export default ContactInformation;
